
export interface AiTool {
  id: string;
  name: string;
  category: string;
  icon: string;
  description: string;
  rating: number;
  reviewCount: number;
  pricing: string;
  url: string;
  features: string[];
  bestFor: string;
}

export interface AiTutorial {
  id: string;
  title: string;
  description: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  duration: string;
  author: { username: string; avatar: string };
  views: number;
  likes: number;
  image: string;
  tags: string[];
  steps: string[];
}

export const aiTools: AiTool[] = [
  {
    id: 'tool-001',
    name: 'Runway Gen-4.5',
    category: 'Video Generation',
    icon: 'https://public.readdy.ai/ai/img_res/fd8db63508a14f8346036650242ef5e1.jpg',
    description: 'Industry-leading AI video generation with Infinite Character Consistency. Top-ranked on Video Arena leaderboard.',
    rating: 4.8,
    reviewCount: 1247,
    pricing: 'From $12/mo',
    url: 'https://runway.ml',
    features: ['Infinite Character Consistency', 'Motion Brush', 'Camera Controls', 'Gen-4 Turbo'],
    bestFor: 'Character-consistent drama scenes'
  },
  {
    id: 'tool-002',
    name: 'OpenAI Sora 2',
    category: 'Video Generation',
    icon: 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20logo%20icon%20for%20OpenAI%20video%20platform%20dark%20gradient%20background%20with%20circular%20swirl%20pattern%20modern%20tech%20aesthetic&width=80&height=80&seq=tool2&orientation=squarish',
    description: 'Generates synchronized dialogue and sound effects. Cameo feature inserts real people into AI scenes.',
    rating: 4.6,
    reviewCount: 2345,
    pricing: 'From $20/mo',
    url: 'https://sora.com',
    features: ['Native Audio/Dialogue', 'Cameo Feature', 'Storyboard Mode', 'Re-cut Tool'],
    bestFor: 'Dialogue-heavy drama scenes'
  },
  {
    id: 'tool-003',
    name: 'Kling AI',
    category: 'Video Generation',
    icon: 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20logo%20icon%20for%20Chinese%20AI%20video%20tool%20dark%20gradient%20background%20with%20eastern%20inspired%20geometric%20pattern%20modern%20tech%20aesthetic&width=80&height=80&seq=tool3&orientation=squarish',
    description: 'Excellent for culturally authentic visuals with strong motion quality and realistic human movements.',
    rating: 4.5,
    reviewCount: 876,
    pricing: 'Free tier available',
    url: 'https://klingai.com',
    features: ['Realistic Motion', 'Cultural Aesthetics', 'Long-form Video', 'Image-to-Video'],
    bestFor: 'Culturally authentic scenes'
  },
  {
    id: 'tool-004',
    name: 'ElevenLabs',
    category: 'Voice & Audio',
    icon: 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20logo%20icon%20for%20AI%20voice%20synthesis%20tool%20dark%20gradient%20background%20with%20sound%20wave%20pattern%20modern%20tech%20aesthetic&width=80&height=80&seq=tool4&orientation=squarish',
    description: 'Premium AI voice cloning and multi-language dubbing. Industry standard for drama voiceovers.',
    rating: 4.9,
    reviewCount: 3456,
    pricing: 'From $5/mo',
    url: 'https://elevenlabs.io',
    features: ['Voice Cloning', 'Multi-language Dubbing', 'Emotional Control', 'Sound Effects'],
    bestFor: 'Voice acting and dubbing'
  },
  {
    id: 'tool-005',
    name: 'ComfyUI',
    category: 'Workflow & Pipeline',
    icon: 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20logo%20icon%20for%20node-based%20AI%20workflow%20tool%20dark%20gradient%20background%20with%20connected%20nodes%20pattern%20modern%20tech%20aesthetic&width=80&height=80&seq=tool5&orientation=squarish',
    description: 'Node-based workflow for advanced AI video pipelines. Full control over generation process.',
    rating: 4.3,
    reviewCount: 654,
    pricing: 'Free (Open Source)',
    url: 'https://github.com/comfyanonymous/ComfyUI',
    features: ['Node-based Workflow', 'Custom Pipelines', 'Batch Processing', 'Plugin Ecosystem'],
    bestFor: 'Advanced custom workflows'
  },
  {
    id: 'tool-006',
    name: 'Descript',
    category: 'Editing & Post',
    icon: 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20logo%20icon%20for%20text-based%20video%20editing%20tool%20dark%20gradient%20background%20with%20document%20and%20play%20button%20pattern%20modern%20tech%20aesthetic&width=80&height=80&seq=tool6&orientation=squarish',
    description: 'Edit videos by editing text transcripts. Revolutionary approach to post-production.',
    rating: 4.4,
    reviewCount: 1890,
    pricing: 'From $24/mo',
    url: 'https://descript.com',
    features: ['Text-based Editing', 'Auto Transcription', 'Filler Word Removal', 'Screen Recording'],
    bestFor: 'Post-production editing'
  },
  {
    id: 'tool-007',
    name: 'HeyGen',
    category: 'AI Avatars',
    icon: 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20logo%20icon%20for%20AI%20avatar%20generation%20tool%20dark%20gradient%20background%20with%20human%20silhouette%20digital%20pattern%20modern%20tech%20aesthetic&width=80&height=80&seq=tool7&orientation=squarish',
    description: 'Create lifelike AI avatars for faceless content. Perfect for narration and presentation scenes.',
    rating: 4.2,
    reviewCount: 1234,
    pricing: 'From $29/mo',
    url: 'https://heygen.com',
    features: ['Lifelike Avatars', 'Lip Sync', 'Multi-language', 'Custom Avatars'],
    bestFor: 'AI avatar narration'
  },
  {
    id: 'tool-008',
    name: 'OpusClip',
    category: 'Content Repurposing',
    icon: 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20logo%20icon%20for%20AI%20content%20repurposing%20tool%20dark%20gradient%20background%20with%20scissors%20and%20film%20strip%20pattern%20modern%20tech%20aesthetic&width=80&height=80&seq=tool8&orientation=squarish',
    description: 'Automatically turns long-form videos into viral short clips. AI-powered highlight detection.',
    rating: 4.1,
    reviewCount: 987,
    pricing: 'From $9/mo',
    url: 'https://opus.pro',
    features: ['Auto Clipping', 'Viral Score', 'Caption Generation', 'Multi-platform Export'],
    bestFor: 'Short-form content creation'
  }
];

export const aiTutorials: AiTutorial[] = [
  {
    id: 'tut-001',
    title: 'Create Your First AI Drama in 60 Minutes',
    description: 'A complete beginner-friendly walkthrough from concept to finished episode using Runway and ElevenLabs.',
    difficulty: 'Beginner',
    duration: '60 min',
    author: {
      username: 'AI Creator Pro',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20young%20tech-savvy%20Middle%20Eastern%20man%20with%20innovative%20expression%20wearing%20modern%20creative%20attire%20studio%20lighting%20clean%20background&width=80&height=80&seq=tut1a&orientation=squarish'
    },
    views: 45600,
    likes: 2340,
    image: 'https://readdy.ai/api/search-image?query=Modern%20creative%20workspace%20with%20multiple%20screens%20showing%20AI%20video%20editing%20interface%20warm%20ambient%20lighting%20cinematic%20composition%20dark%20moody%20atmosphere%20with%20orange%20accent%20lights&width=400&height=225&seq=tut1&orientation=landscape',
    tags: ['beginner', 'runway', 'elevenlabs', 'first-project'],
    steps: [
      'Write your script and storyboard',
      'Generate character reference images',
      'Create scenes with Runway Gen-4.5',
      'Add voice acting with ElevenLabs',
      'Edit and compile in Descript',
      'Export and publish'
    ]
  },
  {
    id: 'tut-002',
    title: 'Character Consistency Masterclass',
    description: 'Advanced techniques for maintaining character appearance across 50+ scenes using LoRA training and reference images.',
    difficulty: 'Advanced',
    duration: '45 min',
    author: {
      username: 'Consistency Expert',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20young%20Arab%20woman%20with%20technical%20expression%20wearing%20professional%20creative%20attire%20studio%20lighting%20clean%20background&width=80&height=80&seq=tut2a&orientation=squarish'
    },
    views: 32100,
    likes: 1890,
    image: 'https://readdy.ai/api/search-image?query=Split%20screen%20comparison%20showing%20same%20AI%20generated%20character%20in%20different%20scenes%20and%20environments%20consistent%20appearance%20warm%20cinematic%20lighting%20professional%20production%20quality&width=400&height=225&seq=tut2&orientation=landscape',
    tags: ['advanced', 'character-consistency', 'lora', 'workflow'],
    steps: [
      'Create detailed character reference sheets',
      'Train LoRA model on character features',
      'Set up Runway Infinite Consistency',
      'Test across multiple environments',
      'Fine-tune problem areas',
      'Batch generate consistent scenes'
    ]
  },
  {
    id: 'tut-003',
    title: 'Budget Drama Production Under $500',
    description: 'Complete cost breakdown and workflow for producing a 12-episode AI drama series on a tight budget.',
    difficulty: 'Beginner',
    duration: '35 min',
    author: {
      username: 'Budget Creator',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20young%20Middle%20Eastern%20man%20with%20resourceful%20expression%20wearing%20casual%20startup%20attire%20studio%20lighting%20clean%20background&width=80&height=80&seq=tut3a&orientation=squarish'
    },
    views: 28900,
    likes: 1567,
    image: 'https://readdy.ai/api/search-image?query=Flat%20lay%20of%20budget%20planning%20notebook%20with%20calculator%20laptop%20showing%20video%20editing%20software%20coffee%20cup%20minimalist%20desk%20setup%20warm%20natural%20lighting%20overhead%20view&width=400&height=225&seq=tut3&orientation=landscape',
    tags: ['budget', 'affordable', 'indie', 'cost-breakdown'],
    steps: [
      'Plan your series arc and episodes',
      'Choose free and affordable tools',
      'Optimize subscription usage',
      'Batch generate to save credits',
      'Use free music and SFX libraries',
      'Distribute on free platforms'
    ]
  },
  {
    id: 'tut-004',
    title: 'Prompt Engineering for Cinematic Scenes',
    description: 'Master the art of writing prompts that produce Hollywood-quality AI drama scenes every time.',
    difficulty: 'Intermediate',
    duration: '40 min',
    author: {
      username: 'Prompt Master',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20Middle%20Eastern%20man%20with%20expert%20expression%20wearing%20tech%20creative%20attire%20studio%20lighting%20clean%20background&width=80&height=80&seq=tut4a&orientation=squarish'
    },
    views: 38700,
    likes: 2100,
    image: 'https://readdy.ai/api/search-image?query=Dramatic%20cinematic%20scene%20of%20a%20desert%20landscape%20at%20golden%20hour%20with%20silhouetted%20figures%20professional%20film%20quality%20composition%20lens%20flare%20warm%20tones%20epic%20scale&width=400&height=225&seq=tut4&orientation=landscape',
    tags: ['prompts', 'cinematic', 'intermediate', 'techniques'],
    steps: [
      'Understand prompt structure anatomy',
      'Master camera angle descriptions',
      'Control lighting and mood',
      'Write action and movement prompts',
      'Create emotional close-up prompts',
      'Build a reusable prompt library'
    ]
  },
  {
    id: 'tut-005',
    title: 'Arabic Voice Acting with ElevenLabs',
    description: 'How to create authentic Arabic dialogue and emotional voiceovers using AI voice cloning technology.',
    difficulty: 'Intermediate',
    duration: '30 min',
    author: {
      username: 'Voice Tech Expert',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20Middle%20Eastern%20man%20with%20tech-savvy%20expression%20wearing%20modern%20attire%20studio%20lighting%20clean%20background&width=80&height=80&seq=tut5a&orientation=squarish'
    },
    views: 21400,
    likes: 1230,
    image: 'https://readdy.ai/api/search-image?query=Professional%20recording%20studio%20microphone%20with%20sound%20waves%20visualization%20dark%20ambient%20lighting%20warm%20orange%20and%20teal%20accent%20colors%20modern%20audio%20production%20setup&width=400&height=225&seq=tut5&orientation=landscape',
    tags: ['voice', 'arabic', 'elevenlabs', 'dubbing'],
    steps: [
      'Set up ElevenLabs for Arabic',
      'Clone or select voice profiles',
      'Write dialogue with emotion markers',
      'Generate and fine-tune output',
      'Sync audio with video scenes',
      'Add ambient sound effects'
    ]
  },
  {
    id: 'tut-006',
    title: 'Monetizing Your AI Drama Channel',
    description: 'Revenue strategies including YouTube ads, Patreon, licensing, and brand deals for AI content creators.',
    difficulty: 'Intermediate',
    duration: '50 min',
    author: {
      username: 'Monetization Pro',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20young%20Arab%20woman%20with%20business%20savvy%20expression%20wearing%20professional%20modern%20attire%20studio%20lighting%20clean%20background&width=80&height=80&seq=tut6a&orientation=squarish'
    },
    views: 52300,
    likes: 3450,
    image: 'https://readdy.ai/api/search-image?query=Modern%20content%20creator%20setup%20with%20analytics%20dashboard%20on%20screen%20showing%20growth%20charts%20and%20revenue%20metrics%20warm%20ambient%20lighting%20professional%20desk%20setup&width=400&height=225&seq=tut6&orientation=landscape',
    tags: ['monetization', 'youtube', 'patreon', 'business'],
    steps: [
      'Set up YouTube monetization',
      'Create Patreon tiers',
      'Explore licensing opportunities',
      'Pitch to brands and sponsors',
      'Diversify revenue streams',
      'Scale your content operation'
    ]
  }
];

export const toolCategories = [
  { id: 'all', name: 'All Tools', icon: 'ri-apps-line' },
  { id: 'Video Generation', name: 'Video Generation', icon: 'ri-video-line' },
  { id: 'Voice & Audio', name: 'Voice & Audio', icon: 'ri-mic-line' },
  { id: 'Workflow & Pipeline', name: 'Workflow', icon: 'ri-flow-chart' },
  { id: 'Editing & Post', name: 'Editing', icon: 'ri-scissors-cut-line' },
  { id: 'AI Avatars', name: 'AI Avatars', icon: 'ri-user-smile-line' },
  { id: 'Content Repurposing', name: 'Repurposing', icon: 'ri-repeat-line' }
];
